import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Electricity cost = kW × hours × rate ($/kWh)
const APPLIANCES = {
  'House': [
    { name: 'Air Conditioner (Central)', w: 3500, h: 6 },
    { name: 'Air Conditioner (Window)', w: 1000, h: 6 },
    { name: 'Heater (Space)', w: 1500, h: 4 },
    { name: 'Water Heater', w: 4000, h: 3 },
    { name: 'Ceiling Fan', w: 75, h: 8 },
    { name: 'LED Bulb', w: 10, h: 5 },
    { name: 'Incandescent Bulb', w: 60, h: 5 },
    { name: 'TV (LED)', w: 100, h: 5 },
    { name: 'Desktop Computer', w: 200, h: 6 },
    { name: 'Laptop', w: 60, h: 6 },
    { name: 'Router / Modem', w: 15, h: 24 },
    { name: 'Washing Machine', w: 500, h: 1 },
    { name: 'Dryer (Electric)', w: 3000, h: 1 },
    { name: 'Vacuum Cleaner', w: 1400, h: 0.5 },
  ],
  'Kitchen': [
    { name: 'Refrigerator', w: 150, h: 24 },
    { name: 'Deep Freezer', w: 200, h: 24 },
    { name: 'Microwave Oven', w: 1000, h: 0.5 },
    { name: 'Electric Oven', w: 2400, h: 1 },
    { name: 'Toaster', w: 1200, h: 0.2 },
    { name: 'Electric Kettle', w: 1500, h: 0.3 },
    { name: 'Coffee Maker', w: 900, h: 0.3 },
    { name: 'Dishwasher', w: 1800, h: 1 },
    { name: 'Blender', w: 300, h: 0.2 },
    { name: 'Rice Cooker', w: 700, h: 0.5 },
    { name: 'Slow Cooker', w: 200, h: 6 },
    { name: 'Induction Cooktop', w: 1800, h: 1 },
  ],
  'Other': [
    { name: 'Phone Charger', w: 5, h: 3 },
    { name: 'Hair Dryer', w: 1500, h: 0.2 },
    { name: 'Iron', w: 1100, h: 0.5 },
    { name: 'Game Console', w: 200, h: 3 },
    { name: 'Printer', w: 50, h: 0.5 },
    { name: 'Electric Toothbrush', w: 3, h: 0.1 },
    { name: 'EV Charger (Level 2)', w: 7200, h: 4 },
    { name: 'Well Pump', w: 750, h: 1 },
    { name: 'Pool Pump', w: 1500, h: 8 },
    { name: 'Sump Pump', w: 800, h: 0.5 },
  ],
};

export default function ElectricityCalc() {
  const [power, setPower] = useState(100);
  const [powerUnit, setPowerUnit] = useState('W');
  const [hoursPerDay, setHoursPerDay] = useState(5);
  const [days, setDays] = useState(30);
  const [rate, setRate] = useState(0.12);
  const [category, setCategory] = useState('');
  const [appliance, setAppliance] = useState('');

  const pickAppliance = (cat, name) => {
    setCategory(cat); setAppliance(name);
    if (!cat || !name) return;
    const app = APPLIANCES[cat]?.find(a => a.name === name);
    if (app) { setPower(app.w); setPowerUnit('W'); setHoursPerDay(app.h); }
  };

  const result = useMemo(() => {
    const kW = powerUnit === 'W' ? Number(power) / 1000 : Number(power);
    const kwh = kW * Number(hoursPerDay) * Number(days);
    const cost = kwh * Number(rate);
    return { kW, kwh, cost };
  }, [power, powerUnit, hoursPerDay, days, rate]);

  return (
    <CalcShell look="cnet" title="Electricity Cost Calculator" subtitle="Estimate energy consumption and running cost for any electrical appliance.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="mb-3 p-3 rounded-xl bg-blue-50 border border-blue-100">
            <label className="text-xs text-slate-600 block mb-1">Pick a typical appliance (auto-fills wattage & hours):</label>
            <div className="grid grid-cols-2 gap-2">
              <select value={category} onChange={e => { setCategory(e.target.value); setAppliance(''); }} data-testid="el-cat"
                className="px-2 py-1.5 rounded border border-slate-300 bg-white text-sm">
                <option value="">— Category —</option>
                {Object.keys(APPLIANCES).map(k => <option key={k} value={k}>{k}</option>)}
              </select>
              <select value={appliance} onChange={e => pickAppliance(category, e.target.value)} disabled={!category} data-testid="el-app"
                className="px-2 py-1.5 rounded border border-slate-300 bg-white text-sm disabled:bg-slate-100">
                <option value="">— Appliance —</option>
                {(APPLIANCES[category] || []).map(a => <option key={a.name} value={a.name}>{a.name}</option>)}
              </select>
            </div>
            {appliance && <p className="text-xs text-slate-500 mt-1">Values are editable below.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Appliance Power</label><input type="number" value={power} onChange={e => setPower(e.target.value)} data-testid="el-power"/></div>
            <div className="field"><label>Unit</label><select value={powerUnit} onChange={e => setPowerUnit(e.target.value)}><option>W</option><option>kW</option></select></div>
          </div>
          <div className="field"><label>Hours used per day</label><input type="number" value={hoursPerDay} onChange={e => setHoursPerDay(e.target.value)}/></div>
          <div className="field"><label>Days</label><input type="number" value={days} onChange={e => setDays(e.target.value)}/></div>
          <div className="field"><label>Electricity Rate ($/kWh)</label><input type="number" step="0.001" value={rate} onChange={e => setRate(e.target.value)} data-testid="el-rate"/></div>

          <div className="mt-4">
            <svg viewBox="0 0 400 110" className="w-full">
              <rect x="10" y="45" width="70" height="40" rx="6" fill="#e0f2fe" stroke="#0284c7"/>
              <text x="45" y="70" textAnchor="middle" className="text-xs" fill="#075985">Meter</text>
              <line x1="80" y1="65" x2="200" y2="65" stroke="#0f172a" strokeWidth="3"/>
              <rect x="200" y="35" width="80" height="60" rx="6" fill="#fef3c7" stroke="#b45309"/>
              <text x="240" y="70" textAnchor="middle" className="text-xs" fill="#78350f">Appliance</text>
              <text x="240" y="55" textAnchor="middle" className="text-[10px]" fill="#78350f">{result.kW.toFixed(3)} kW</text>
              <text x="140" y="55" textAnchor="middle" className="text-xs font-mono" fill="#0f172a">Line</text>
            </svg>
          </div>
        </div>
        <div>
          <div className="p-5 rounded-xl bg-sky-50 border border-sky-200 text-center">
            <div className="text-sm text-slate-600">Energy Used</div>
            <div className="text-4xl font-bold text-sky-700" data-testid="el-kwh">{result.kwh.toFixed(2)} kWh</div>
            <div className="mt-3 text-sm text-slate-600">Total Cost</div>
            <div className="text-3xl font-bold">${result.cost.toFixed(2)}</div>
          </div>
        </div>
      </div>
    </CalcShell>
  );
}
